import { useState } from 'react';
import { useLoyalty } from '@/contexts/LoyaltyContext';
import { Button } from '@/components/ui/button';
import { Trophy, Gift, Loader2, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface LoyaltyPointsRedemptionProps {
  cartTotal: number;
  onApplyDiscount: (discountAmount: number, pointsUsed: number) => void;
  appliedPoints?: number;
  onRemoveDiscount?: () => void;
}

const LoyaltyPointsRedemption = ({ 
  cartTotal, 
  onApplyDiscount, 
  appliedPoints = 0,
  onRemoveDiscount 
}: LoyaltyPointsRedemptionProps) => {
  const { points, user, isLoading: loyaltyLoading, tier } = useLoyalty();
  const [isApplying, setIsApplying] = useState(false);

  // Conversion rate: 100 points = $1.00, 1000 points = $10.00
  const POINTS_PER_DOLLAR = 100;
  const MIN_POINTS_TO_REDEEM = 100;

  // Calculate max redeemable points (can't redeem more than cart total)
  const maxRedeemableValue = Math.floor(cartTotal);
  const maxRedeemablePoints = maxRedeemableValue * POINTS_PER_DOLLAR;
  const pointsToUse = Math.min(points, maxRedeemablePoints);
  const discountValue = pointsToUse / POINTS_PER_DOLLAR;

  const canRedeem = user && points >= MIN_POINTS_TO_REDEEM && pointsToUse >= MIN_POINTS_TO_REDEEM;

  const handleApplyPoints = async () => {
    if (!canRedeem) return;

    setIsApplying(true);
    
    // Simulate processing time
    await new Promise(resolve => setTimeout(resolve, 500));
    
    onApplyDiscount(discountValue, pointsToUse);
    toast.success(`Applied ${pointsToUse} points for $${discountValue.toFixed(2)} off!`, {
      position: 'top-center'
    });
    
    setIsApplying(false);
  };

  const handleRemovePoints = () => {
    if (onRemoveDiscount) {
      onRemoveDiscount();
      toast.success('Points discount removed', { position: 'top-center' });
    }
  };

  // Don't show if user is not logged in
  if (!user) {
    return (
      <div className="bg-muted/30 border border-border/50 rounded-lg p-3">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Trophy className="w-4 h-4" />
          <span className="text-sm">Sign in to redeem rewards points</span>
        </div>
      </div>
    );
  }

  // Loading state
  if (loyaltyLoading) {
    return (
      <div className="bg-muted/30 border border-border/50 rounded-lg p-3">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading rewards...</span>
        </div>
      </div>
    );
  }

  // Points already applied
  if (appliedPoints > 0) {
    const appliedValue = appliedPoints / POINTS_PER_DOLLAR;
    return (
      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-600" />
            <div>
              <span className="text-sm font-medium text-green-600">
                {appliedPoints.toLocaleString()} points applied
              </span>
              <p className="text-xs text-green-600/80">
                -${appliedValue.toFixed(2)} discount
              </p>
            </div>
          </div>
          {onRemoveDiscount && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemovePoints}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Remove
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-full bg-primary/20">
            <Trophy className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">
              {tier} Rewards
            </p>
            <p className="text-xs text-muted-foreground">
              {points.toLocaleString()} points available
            </p>
          </div>
        </div>
        
        {canRedeem && (
          <div className="text-right">
            <p className="text-sm font-bold text-primary">
              -${discountValue.toFixed(2)}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {pointsToUse.toLocaleString()} pts
            </p>
          </div>
        )}
      </div>

      {canRedeem ? (
        <Button
          onClick={handleApplyPoints}
          variant="outline"
          size="sm"
          className="w-full border-primary/30 hover:bg-primary/10"
          disabled={isApplying}
        >
          {isApplying ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Applying...
            </>
          ) : (
            <>
              <Gift className="w-4 h-4 mr-2" />
              Redeem {pointsToUse.toLocaleString()} Points
            </>
          )}
        </Button>
      ) : (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <AlertCircle className="w-3 h-3" />
          <span>
            {points < MIN_POINTS_TO_REDEEM 
              ? `Need ${MIN_POINTS_TO_REDEEM - points} more points to redeem`
              : 'Earn more points by shopping!'}
          </span>
        </div>
      )}

      {/* Points you'll earn on this order */}
      <div className="pt-2 border-t border-primary/10">
        <p className="text-xs text-muted-foreground">
          <span className="text-primary font-medium">
            +{Math.floor(cartTotal * 10)} points
          </span>
          {' '}will be earned on this order
        </p>
      </div>
    </div>
  );
};

export default LoyaltyPointsRedemption;